import * as React from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  MdArrowDownward,
  MdArrowUpward,
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdFilterAlt,
  MdKeyboardArrowLeft,
  MdMoreVert,
  MdSearch,
  MdSort,
  MdViewColumn,
  MdVisibilityOff,
} from 'react-icons/md';



const OPERATORS = [
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'notContains', label: 'does not contain', needsValue: true },
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'notEquals', label: 'does not equal', needsValue: true },
  { value: 'startsWith', label: 'starts with', needsValue: true },
  { value: 'endsWith', label: 'ends with', needsValue: true },
  { value: 'isEmpty', label: 'is empty', needsValue: false },
  { value: 'isNotEmpty', label: 'is not empty', needsValue: false },
];

const extractTextFromReactNode = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(' ').trim();
  if (React.isValidElement(node)) {
    return extractTextFromReactNode(node.props?.children);
  }
  return '';
};

const getColumnValue = (column, row, mode = 'sort') => {
  if (!column || !row) return '';
  if (mode === 'filter' && typeof column.filterAccessor === 'function') {
    return column.filterAccessor(row);
  }
  if (mode === 'sort' && typeof column.sortAccessor === 'function') {
    return column.sortAccessor(row);
  }
  if (typeof column.accessor === 'function') {
    return column.accessor(row);
  }
  if (typeof column.valueGetter === 'function') {
    return column.valueGetter({ row, value: row[column.id] });
  }

  // Check direct property on row
  if (row[column.id] !== undefined && row[column.id] !== null) {
    return row[column.id];
  }

  // Check case-insensitive property on row
  const colIdLower = String(column.id || '').toLowerCase();
  for (const key of Object.keys(row)) {
    if (key.toLowerCase() === colIdLower && row[key] !== undefined && row[key] !== null) {
      return row[key];
    }
  }

  // If column has format function, invoke it and extract text from returned JSX
  if (typeof column.format === 'function') {
    try {
      const formatted = column.format(row[column.id], row);
      if (typeof formatted === 'string' || typeof formatted === 'number') {
        return formatted;
      }
      if (React.isValidElement(formatted) || Array.isArray(formatted)) {
        const extracted = extractTextFromReactNode(formatted);
        if (extracted) return extracted;
      }
    } catch {
      // fallback
    }
  }

  return '';
};

const rawValue = (column, row) => getColumnValue(column, row, 'sort');

const textValue = (column, row) => {
  const v = getColumnValue(column, row, 'filter');
  return v === null || v === undefined ? '' : String(v).trim();
};

/**
 * Distinct, sorted list of the values that appear in a column across `rows`.
 * A column can expose `optionsAccessor(row) => string[]` when one cell holds
 * several values (e.g. a medication list); otherwise the display/text value is used.
 */
const distinctColumnValues = (column, rows) => {
  const set = new Set();
  (rows || []).forEach((row) => {
    const raw =
      typeof column.optionsAccessor === 'function'
        ? column.optionsAccessor(row)
        : [textValue(column, row)];
    (Array.isArray(raw) ? raw : [raw]).forEach((v) => {
      const s = v === null || v === undefined ? '' : String(v).trim();
      if (s && s !== '—') set.add(s);
    });
  });
  return [...set].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
};

const matchesFilter = (value, { op, term }) => {
  const v = String(value || '').toLowerCase().trim();
  const t = String(term || '').toLowerCase().trim();
  switch (op) {
    case 'contains': return v.includes(t);
    case 'notContains': return !v.includes(t);
    case 'equals': return v === t;
    case 'notEquals': return v !== t;
    case 'startsWith': return v.startsWith(t);
    case 'endsWith': return v.endsWith(t);
    case 'isEmpty': return v === '' || v === '—' || v === '-';
    case 'isNotEmpty': return v !== '' && v !== '—' && v !== '-';
    default: return true;
  }
};

const compareValues = (a, b) => {
  const na = typeof a === 'number' ? a : parseFloat(String(a).replace(/[$,%\s]/g, ''));
  const nb = typeof b === 'number' ? b : parseFloat(String(b).replace(/[$,%\s]/g, ''));
  if (Number.isFinite(na) && Number.isFinite(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return na - nb;
  }
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

const menuAllowed = (column, gridDisabled) => {
  if (gridDisabled || column.disableColumnMenu) return false;
  const id = String(column.id || '').toLowerCase();
  return id !== 'actions' && id !== 'action';
};

/* ----------------------------------------------------------- column menu -- */

function ColumnMenu({ column, columns, rows, hidden, sort, filter, onSort, onFilter, onHide, onToggleColumn }) {
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState('menu'); // menu | filter | columns | search
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const [optQuery, setOptQuery] = React.useState('');
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);

  const active = Boolean(filter) || sort?.colId === column.id;

  const place = React.useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 240;
    setPos({
      top: Math.min(r.bottom + 6, window.innerHeight - 320),
      left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)),
    });
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    place();
    const onDoc = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        popRef.current && !popRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  React.useEffect(() => {
    if (open) setView('menu');
  }, [open]);

  React.useEffect(() => {
    if (view !== 'search') setOptQuery('');
  }, [view]);

  const close = () => setOpen(false);
  const op = filter?.op || 'contains';
  const term = filter?.term || '';
  const needsValue = OPERATORS.find((o) => o.value === op)?.needsValue ?? true;
  const sortedAsc = sort?.colId === column.id && sort.dir === 'asc';
  const sortedDesc = sort?.colId === column.id && sort.dir === 'desc';

  // "Search values" picker — multi-value columns filter with `contains`, others with `equals`.
  const pickOp = typeof column.optionsAccessor === 'function' ? 'contains' : 'equals';
  const activePick = filter && filter.op === pickOp ? filter.term : null;
  const searchOptions = React.useMemo(
    () => (view === 'search' ? distinctColumnValues(column, rows) : []),
    [view, column, rows],
  );
  const shownOptions = React.useMemo(() => {
    const q = optQuery.trim().toLowerCase();
    return q ? searchOptions.filter((o) => o.toLowerCase().includes(q)) : searchOptions;
  }, [searchOptions, optQuery]);

  const itemCls = 'dropdown-item d-flex align-items-center gap-2 rounded-2 px-2 py-2';
  const itemStyle = (on) => ({ fontSize: '0.85rem', color: on ? 'var(--hc-accent-teal)' : undefined, fontWeight: on ? 600 : 400 });

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        title="Column options"
        aria-label={`${column.label} column options`}
        className="btn btn-sm p-1 lh-1 border-0 d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
        style={{
          backgroundColor: active ? 'var(--hc-accent-teal)' : 'transparent',
          color: active ? '#fff' : 'var(--hc-text-muted-slate, #64748b)',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <MdMoreVert size={16} style={{ opacity: active ? 1 : 0.65 }} />
      </button>

      {open
        ? createPortal(
          <div
            ref={popRef}
            className="bg-white border rounded-3 shadow-lg text-start"
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1400, width: 240 }}
          >
            <div
              className="d-flex align-items-center justify-content-between px-2 py-1 border-bottom"
              style={{ fontSize: '0.66rem', letterSpacing: '0.05em' }}
            >
              {view !== 'menu' ? (
                <button
                  type="button"
                  className="btn btn-sm p-0 border-0 bg-transparent text-secondary d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '0.72rem' }}
                  onClick={() => setView('menu')}
                >
                  <MdKeyboardArrowLeft size={16} /> Back
                </button>
              ) : (
                <span className="fw-semibold text-secondary text-uppercase text-truncate">{column.label}</span>
              )}
              <button type="button" className="btn btn-sm p-0 border-0 bg-transparent text-secondary" onClick={close}>
                <MdClose size={15} />
              </button>
            </div>

            {view === 'menu' ? (
              <div className="p-1">
                <button type="button" className={itemCls} style={itemStyle(sortedAsc)} onClick={() => { onSort(sortedAsc ? null : { colId: column.id, dir: 'asc' }); close(); }}>
                  <MdArrowUpward size={15} /> Sort ascending
                </button>
                <button type="button" className={itemCls} style={itemStyle(sortedDesc)} onClick={() => { onSort(sortedDesc ? null : { colId: column.id, dir: 'desc' }); close(); }}>
                  <MdArrowDownward size={15} /> Sort descending
                </button>
                {sort?.colId === column.id ? (
                  <button type="button" className={itemCls} style={itemStyle(false)} onClick={() => { onSort(null); close(); }}>
                    <MdSort size={15} /> Unsort
                  </button>
                ) : null}
                <hr className="dropdown-divider my-1" />
                <button type="button" className={itemCls} style={itemStyle(Boolean(filter))} onClick={() => setView('filter')}>
                  <MdFilterAlt size={15} /> Filter{filter ? ' (active)' : ''}
                </button>
                <button type="button" className={itemCls} style={itemStyle(Boolean(activePick))} onClick={() => setView('search')}>
                  <MdSearch size={15} /> Search values{activePick ? ' (active)' : ''}
                </button>
                <hr className="dropdown-divider my-1" />
                <button type="button" className={itemCls} style={itemStyle(false)} onClick={() => { onHide(column.id); close(); }}>
                  <MdVisibilityOff size={15} /> Hide column
                </button>
                <button type="button" className={itemCls} style={itemStyle(false)} onClick={() => setView('columns')}>
                  <MdViewColumn size={15} /> Manage columns
                </button>
              </div>
            ) : null}

            {view === 'filter' ? (
              <div className="p-2">
                <select
                  className="form-select form-select-sm mb-2"
                  value={op}
                  onChange={(e) => {
                    const nextOp = e.target.value;
                    const nv = OPERATORS.find((o) => o.value === nextOp)?.needsValue ?? true;
                    if (!nv) onFilter(column.id, { op: nextOp, term: '' });
                    else onFilter(column.id, term ? { op: nextOp, term } : null);
                  }}
                >
                  {OPERATORS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {needsValue ? (
                  <input
                    type="text"
                    autoFocus
                    className="form-control form-control-sm"
                    placeholder="Filter value"
                    value={term}
                    onChange={(e) => {
                      const v = e.target.value;
                      onFilter(column.id, v ? { op, term: v } : null);
                    }}
                  />
                ) : null}
                {filter ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 mt-2 text-decoration-none"
                    style={{ fontSize: '0.78rem' }}
                    onClick={() => { onFilter(column.id, null); setView('menu'); }}
                  >
                    Clear filter
                  </button>
                ) : null}
              </div>
            ) : null}

            {view === 'search' ? (
              <div className="p-2">
                <input
                  type="text"
                  autoFocus
                  className="form-control form-control-sm mb-2"
                  placeholder={`Search ${column.label}…`}
                  value={optQuery}
                  onChange={(e) => setOptQuery(e.target.value)}
                />
                <div className="border rounded-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {shownOptions.length === 0 ? (
                    <div className="text-secondary text-center py-3" style={{ fontSize: '0.8rem' }}>
                      No values
                    </div>
                  ) : (
                    shownOptions.map((opt) => {
                      const on = activePick != null && activePick.toLowerCase() === opt.toLowerCase();
                      return (
                        <button
                          key={opt}
                          type="button"
                          className="dropdown-item d-flex align-items-center gap-2 px-2 py-2"
                          style={{ fontSize: '0.82rem', color: on ? 'var(--hc-accent-teal)' : undefined, fontWeight: on ? 600 : 400 }}
                          title={opt}
                          onClick={() => onFilter(column.id, on ? null : { op: pickOp, term: opt })}
                        >
                          <MdCheck size={14} style={{ opacity: on ? 1 : 0, flexShrink: 0 }} />
                          <span className="text-truncate">{opt}</span>
                        </button>
                      );
                    })
                  )}
                </div>
                {activePick != null ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 mt-2 text-decoration-none"
                    style={{ fontSize: '0.78rem' }}
                    onClick={() => onFilter(column.id, null)}
                  >
                    Clear selection
                  </button>
                ) : null}
              </div>
            ) : null}

            {view === 'columns' ? (
              <div className="p-1" style={{ maxHeight: 240, overflowY: 'auto' }}>
                {columns.map((c) => (
                  <label
                    key={c.id}
                    className="dropdown-item d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-0"
                    style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={!hidden.includes(c.id)}
                      onChange={() => onToggleColumn(c.id)}
                    />
                    <span className="text-truncate">{c.label || c.id}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>,
          document.body,
        )
        : null}
    </>
  );
}

/* ---------------------------------------------------------------- grid ---- */

const DataGrid = React.memo(function DataGrid({
  columns = [],
  rows = [],
  rowsPerPageOptions = [5, 10, 25, 100],
  defaultRowsPerPage = 5,
  maxHeight = 455,
  stickyHeader = true,
  hidePaginationWhenSinglePage = true,
  getRowId,
  loading = false,
  skeletonRows,
  disableColumnMenu = false,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(defaultRowsPerPage);
  const [filterModel, setFilterModel] = React.useState({});
  const [sortModel, setSortModel] = React.useState(null);
  const [hiddenCols, setHiddenCols] = React.useState([]);

  const setFilter = React.useCallback((colId, model) => {
    setFilterModel((prev) => {
      const next = { ...prev };
      if (model) next[colId] = model;
      else delete next[colId];
      return next;
    });
    setPage(0);
  }, []);

  const toggleColumn = React.useCallback((colId) => {
    setHiddenCols((prev) => (prev.includes(colId) ? prev.filter((c) => c !== colId) : [...prev, colId]));
  }, []);

  const visibleColumns = React.useMemo(
    () => columns.filter((c) => !hiddenCols.includes(c.id)),
    [columns, hiddenCols],
  );

  const processedRows = React.useMemo(() => {
    let out = rows;
    const activeFilters = Object.entries(filterModel).filter(([, m]) => m);
    if (activeFilters.length) {
      out = out.filter((row) =>
        activeFilters.every(([colId, m]) => {
          const column = columns.find((c) => c.id === colId);
          return column ? matchesFilter(textValue(column, row), m) : true;
        }),
      );
    }
    if (sortModel) {
      const column = columns.find((c) => c.id === sortModel.colId);
      if (column) {
        const dir = sortModel.dir === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => compareValues(rawValue(column, a), rawValue(column, b)) * dir);
      }
    }
    return out;
  }, [rows, columns, filterModel, sortModel]);

  const totalPages = React.useMemo(
    () => Math.max(1, Math.ceil(processedRows.length / rowsPerPage)),
    [processedRows.length, rowsPerPage],
  );
  const shouldShowPagination = !hidePaginationWhenSinglePage || processedRows.length > rowsPerPage;

  React.useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const visibleRows = React.useMemo(
    () => processedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [processedRows, page, rowsPerPage],
  );

  const skelCount = skeletonRows || Math.min(defaultRowsPerPage, 8);
  const hasActiveFilter = Object.values(filterModel).some(Boolean);

  return (
    <Paper
      elevation={0}
      sx={{ width: '100%', overflow: 'hidden', border: '1px solid var(--hc-table-border)', borderRadius: 3 }}
    >
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader={stickyHeader} aria-label="data grid" size="small">
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => {
                const withMenu = menuAllowed(column, disableColumnMenu);
                return (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
                    sx={{
                      backgroundColor: 'var(--hc-table-header-bg)',
                      color: 'var(--hc-table-header-text)',
                      fontWeight: 700,
                      borderBottom: '1px solid var(--hc-table-header-border)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100 gap-1">
                      <span
                        className={`text-truncate ${column.align === 'center'
                            ? 'text-center flex-grow-1'
                            : column.align === 'right'
                              ? 'text-end flex-grow-1'
                              : 'flex-grow-1'
                          }`}
                        style={{ minWidth: 0 }}
                      >
                        {column.label}
                      </span>
                      {withMenu ? (
                        <div className="ms-auto flex-shrink-0 d-inline-flex align-items-center">
                          <ColumnMenu
                            column={column}
                            columns={columns}
                            rows={rows}
                            hidden={hiddenCols}
                            sort={sortModel}
                            filter={filterModel[column.id]}
                            onSort={setSortModel}
                            onFilter={setFilter}
                            onHide={toggleColumn}
                            onToggleColumn={toggleColumn}
                          />
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(skelCount)].map((_, r) => (
                <TableRow key={`skel-${r}`}>
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
                      sx={{ borderBottom: '1px solid var(--hc-table-cell-border)' }}
                    >
                      <span
                        className="skeleton rounded-2 d-block"
                        style={{
                          height: 14,
                          width: String(column.id).toLowerCase().includes('action') ? 72 : '72%',
                          marginLeft: column.align === 'center' ? 'auto' : 0,
                          marginRight: column.align === 'center' ? 'auto' : 0,
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <>
                {visibleRows.map((row, index) => {
                  const rowId =
                    (typeof getRowId === 'function' && getRowId(row, index)) ||
                    row.id ||
                    row.code ||
                    `${page}-${index}`;
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={rowId}
                      sx={{ '&:nth-of-type(even)': { backgroundColor: 'var(--hc-table-striped-row-bg)' } }}
                    >
                      {visibleColumns.map((column) => {
                        const value = row[column.id];
                        const displayValue = column.format ? column.format(value, row) : value;
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{ borderBottom: '1px solid var(--hc-table-cell-border)' }}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length || 1}
                      align="center"
                      sx={{ py: 4, color: 'var(--hc-status-inactive-text)' }}
                    >
                      {hasActiveFilter ? 'No rows match the column filters' : 'No records found'}
                    </TableCell>
                  </TableRow>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && shouldShowPagination ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            px: 2,
            py: 1,
            borderTop: '1px solid var(--hc-table-border)',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" sx={{ color: 'var(--hc-table-footer-text)' }}>
            {processedRows.length === 0
              ? '0-0 of 0'
              : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, processedRows.length)} of ${processedRows.length}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous page">
              <MdChevronLeft size={20} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </IconButton>
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
});

export default DataGrid;
